import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {
  GridActionsCellItem,
  GridRenderCellParams,
  GridActionsCellItemProps,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { ProtectedContent } from '../../components/ProtectedContent';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { usePermissions, useSearchParamsState } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { authSelector } from '../../redux/features/auth/auth.selectors';
import {
  cleanEmployees,
  fetchEmployees,
  resetEmployeePassword,
  updateEmployeeStatus,
} from '../../redux/modules/employee/employee.actions';
import { employeeSelector } from '../../redux/modules/employee/employee.selectors';
import { locations } from '../../routes/locations';
import { Employee, EmployeeStatus } from '../../types/employee';
import { parseJSON } from '../../utils/json';
import * as permissions from '../../utils/permissions';
import { GridActionsCellItemLink } from '../../components/FakeLink';

export function EmployeeList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const tableRef = useRef<TableRef>(null);
  const [selectedRow, setSelectedRow] = useState<Employee>();
  const [actionMenu, setActionMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 80,
    },
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      width: 150,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      type: 'string',
      width: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      type: 'string',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      minWidth: 200,
      sortable: false,
      valueFormatter: (params) => params.value?.name,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'string',
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => {
        const color = params.value === EmployeeStatus.ACTIVE ? 'success' : 'error';
        const Icon = params.value === EmployeeStatus.ACTIVE ? CheckCircleIcon : RemoveCircleIcon;

        return <Chip variant="outlined" color={color} icon={<Icon />} label={params.value} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
    },
  ]);
  const canRead = usePermissions({ items: [permissions.Employee.read] });
  const canDoActions = usePermissions({
    items: [permissions.Employee.statusUpdate, permissions.Employee.resetPassword],
    checkType: 'some',
  });
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '100');

  const { totalEmployees, employees, fetchStatus, updateStatusStatus, resetPasswordStatus } =
    useSelector(employeeSelector);
  const { user: authUser } = useSelector(authSelector);
  const loading = fetchStatus === fetchStatuses.pending;
  const resetPasswordLoading = resetPasswordStatus === fetchStatuses.pending;
  const updateStatusLoading = updateStatusStatus === fetchStatuses.pending;
  const newStatus = selectedRow?.status === EmployeeStatus.ACTIVE ? EmployeeStatus.INACTIVE : EmployeeStatus.ACTIVE;
  const statusTitle = selectedRow?.status === EmployeeStatus.ACTIVE ? 'Deactivate' : 'Activate';

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, data: Employee) => {
    setSelectedRow(data);
    setActionMenu(actionMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
  };

  const handleCloseMenu = () => {
    setActionMenu(null);
  };

  const resetPassword = (id: number) => {
    dispatch(resetEmployeePassword(id));
    handleCloseMenu();
  };

  const updateStatus = (id: number, status: EmployeeStatus) => {
    dispatch(updateEmployeeStatus({ id, status }));
    handleCloseMenu();
  };

  const onAddNew = (): void => {
    navigate(locations.employee(0));
  };

  useEffect(
    () => () => {
      dispatch(cleanEmployees());
    },
    [],
  );

  useEffect(() => {
    if (updateStatusStatus === fetchStatuses.success) {
      tableRef?.current?.reload();
      enqueueSnackbar('Employee status updated!', { variant: 'success' });
    }
    if (updateStatusStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Employee update status error!', { variant: 'error' });
    }
    if (resetPasswordStatus === fetchStatuses.success) {
      enqueueSnackbar('Reset employee password was successful!', {
        variant: 'success',
      });
    }
    if (resetPasswordStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Reset employee password error!', { variant: 'error' });
    }
  }, [updateStatusStatus, resetPasswordStatus]);

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canRead) {
          actions.push(
            <GridActionsCellItemLink
              key={`employee-list-read-${params.row.id}`}
              icon={<OpenInNewIcon />}
              label="Link"
              href={locations.employee(params.row.id)}
            />,
          );
        }

        if (canDoActions) {
          actions.push(
            <GridActionsCellItem
              key={`employee-list-actions-${params.row.id}`}
              icon={<MoreVertIcon />}
              label="Actions"
              onClick={(e) => handleMenuOpen(e, params.row)}
            />,
          );
        }

        return actions;
      },
    };

    if (canRead || canDoActions) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canRead, canDoActions]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Employees &nbsp;
          <ProtectedContent items={[permissions.User.create]}>
            <Fab color="primary" size="small" aria-label="Create Employee" onClick={onAddNew}>
              <AddIcon />
            </Fab>
          </ProtectedContent>
        </Typography>
      </Box>
      <Table
        ref={tableRef}
        fetchItems={fetchEmployees}
        rows={employees}
        columns={columns}
        loading={loading}
        rowCount={totalEmployees}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
      />
      <Menu
        open={actionMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={actionMenu !== null ? { top: actionMenu.mouseY, left: actionMenu.mouseX } : undefined}
      >
        <ProtectedContent items={[permissions.Employee.resetPassword]}>
          <MenuItem onClick={() => selectedRow && resetPassword(selectedRow.id)} disabled={resetPasswordLoading}>
            Reset password
          </MenuItem>
        </ProtectedContent>
        <ProtectedContent items={[permissions.Employee.statusUpdate]}>
          <MenuItem
            onClick={() => selectedRow && updateStatus(selectedRow.id, newStatus)}
            disabled={updateStatusLoading || selectedRow?.id === authUser?.id}
          >
            {statusTitle}
          </MenuItem>
        </ProtectedContent>
      </Menu>
    </Container>
  );
}
